import { Dispatch, forwardRef, SetStateAction } from 'react';
import { AssistantSmartTipsEntity, Customer } from '@mews-commander/data-access';
import { FeatureFlag } from '@mews-commander/enums';
import { useFeatureFlag } from '@mews/framework';
import { Stack } from '@optimus-web/core';
import { SmartTips } from '../SmartTips';
import { SummaryComponent, useSummarySectionOrderQuery } from '../../query/useSummarySectionOrderQuery';
import { SearchType } from '../../assistantSearchTypes';
import { ActiveReservationProps } from '../hooks/useSummaryCustomerHook';
import { GuestValueSection } from '../../../../accounts/modules/guestSummaryPanel';
import { CustomerInfo } from './CustomerInfo';
import { CustomerReservations } from './CustomerReservations';
import { CustomerBills } from './CustomerBills';

interface CustomerSummaryProps {
    itemId: string;
    chainId: string;
    entity: AssistantSmartTipsEntity;
    enterpriseIds: Array<string>;
    setActiveReservationState: Dispatch<SetStateAction<ActiveReservationProps>>;
    customer: Customer;
}

export const CustomerSummary = forwardRef<HTMLDivElement, CustomerSummaryProps>((props: CustomerSummaryProps, ref) => {
    const { itemId, chainId, customer, entity, enterpriseIds, setActiveReservationState } = props;

    const assistantSummaryGuestInsightsEnabled = useFeatureFlag(FeatureFlag.assistantSummaryGuestInsightsEnabled);

    const { data: result } = useSummarySectionOrderQuery(SearchType.Customer, itemId);

    if (!result) {
        return null;
    }
    const summaryComponentsOrder = result.components;

    const customerInfo = <CustomerInfo customer={customer} key={SummaryComponent.CustomerInfo} />;
    const smartTips = (
        <SmartTips
            itemId={itemId}
            chainId={chainId}
            entity={entity}
            enterpriseIds={enterpriseIds}
            key={SummaryComponent.SmartTips}
        />
    );

    const reservations = (
        <CustomerReservations
            customerId={itemId}
            setActiveReservationState={setActiveReservationState}
            key={SummaryComponent.Reservations}
        />
    );

    const customerInsights = <GuestValueSection guestId={itemId} key={SummaryComponent.CustomerInsights} />;

    const bills = <CustomerBills customerId={itemId} enterpriseIds={enterpriseIds} key={SummaryComponent.Bills} />;

    const customerSummaryComponents = {
        [SummaryComponent.CustomerInfo]: customerInfo,
        [SummaryComponent.SmartTips]: smartTips,
        [SummaryComponent.CustomerInsights]: assistantSummaryGuestInsightsEnabled ? customerInsights : null,
        [SummaryComponent.Reservations]: reservations,
        [SummaryComponent.Bills]: bills,
    };

    return (
        <Stack ref={ref} fill vertical spacing="200" distribution="stretch">
            {summaryComponentsOrder.map((component) => customerSummaryComponents[component])}
        </Stack>
    );
});
